import { IS_PRODUCTION } from '@/shared/lib'

import type { IOneLinkConfig } from './types'

/**
 * 정비소·차종 검색이 같은 템플릿을 쓰고 트래킹 파라미터로만 흐름을 나눈다 (WEB-4236).
 * 운영 외 환경에서 운영 템플릿을 쓰면 dev 앱이 있어도 App Store로 빠진다 (onelink/README).
 */
const SEO_ONELINK_BASE_URL = IS_PRODUCTION
  ? 'https://web-seo-geo.onelink.me/kFy7'
  : 'https://web-dev-seo-geo.onelink.me/hCvn'

const V2_MEDIA_SOURCE = 'seo_geo_2026'
const V2_AF_SUB1 = 'v2'
const SEO_DEEP_LINK_VALUE = 'home'

/** URL 쿼리(`key`) 값이 없으면 `defaultValue`를 쓰고, 빈 문자열이면 최종 URL에서 빠진다 */
const afParam = (key: string, defaultValue: string) => ({
  keys: [key],
  defaultValue,
})

const afExcluded = (key: string) => afParam(key, '')

interface ISeoOneLinkConfigParams {
  campaign: string
  channel: string
  afSub2: string
}

/** af_sub3(정비소·차종 ID)는 클릭 시점에 `useOneLink.generateLink({ afSub3 })`가 넣고, V2에서 안 쓰는 칸은 빈 값으로 빼낸다 */
const createSeoOneLinkConfig = ({
  campaign,
  channel,
  afSub2,
}: ISeoOneLinkConfigParams): IOneLinkConfig => ({
  oneLinkURL: SEO_ONELINK_BASE_URL,
  afParameters: {
    mediaSource: afParam('af_pid', V2_MEDIA_SOURCE),
    campaign: afParam('af_c', campaign),
    channel: afParam('af_channel', channel),
    adSet: afExcluded('af_adset'),
    ad: afExcluded('af_ad'),
    afSub1: afParam('af_sub1', V2_AF_SUB1),
    afSub2: afParam('af_sub2', afSub2),
    afSub3: afExcluded('af_sub3'),
    deepLinkValue: afParam('deep_link_value', SEO_DEEP_LINK_VALUE),
    afCustom: [],
  },
})

export const SEO_ONELINK_CONFIG = createSeoOneLinkConfig({
  campaign: 'v2_place_list',
  channel: 'v2_place',
  afSub2: 'place',
})

export const CAR_HUB_ONELINK_CONFIG = createSeoOneLinkConfig({
  campaign: 'v2_carhub_list',
  channel: 'v2_carhub',
  afSub2: 'carhub',
})

/** useOneLink가 afParameters의 중첩 구조를 알지 않도록 여기서 af_sub3를 넣는다 */
export const withAfSub3 = (
  config: IOneLinkConfig,
  afSub3: string
): IOneLinkConfig => ({
  ...config,
  afParameters: {
    ...config.afParameters,
    afSub3: { ...config.afParameters.afSub3, defaultValue: afSub3 },
  },
})

interface IPromotionUtm {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
}

interface IPromotionOneLinkParams {
  partner: string
  utm?: IPromotionUtm
}

/** ⚠️ 광고 유입이 SEO 집계(`web-seo-geo`)에 섞이지 않도록 기존 이벤트 랜딩과 같은 템플릿을 쓴다 */
const PROMOTION_ONELINK_BASE_URL = 'https://mycle-ss.onelink.me/aOTz'

/** ⚠️ 스킴 없이 `home`만 주면 앱이 첫 화면에서 멈추거나 스토어로 빠진다 */
const PROMOTION_DEEP_LINK = 'macarongapp:///home'

/**
 * ⚠️ 카카오 로그인을 다녀오면 주소의 `utm_*`이 사라져서, 들어올 때 저장한 UTM을 `defaultValue`로 넣는다.
 * 값이 없을 때 빈 문자열로 두면 칸이 URL에서 빠지므로 제휴처 이름으로 채운다.
 */
export const createPromotionOneLinkConfig = ({
  partner,
  utm,
}: IPromotionOneLinkParams): IOneLinkConfig => ({
  oneLinkURL: PROMOTION_ONELINK_BASE_URL,
  afParameters: {
    mediaSource: afParam('af_pid', utm?.utmSource || partner),
    campaign: afParam('af_c', utm?.utmCampaign || partner),
    channel: afParam('af_channel', utm?.utmMedium || partner),
    // 광고 소재를 구분하려면 둘 다 채워야 한다
    adSet: afParam('af_adset', utm?.utmContent || partner),
    ad: afParam('af_ad', utm?.utmContent || partner),
    afSub1: afParam('af_sub1', V2_AF_SUB1),
    afSub2: afParam('af_sub2', partner),
    afSub3: afExcluded('af_sub3'),
    deepLinkValue: afParam('deep_link_value', PROMOTION_DEEP_LINK),
    afCustom: [],
  },
})
